import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  aplicarTemaNoDocumento,
  gravarTemaEscolhido,
  lerTemaEscolhido,
  limparTemaEscolhido,
  temaDoSistema,
  type Tema,
} from '@/lib/temaPreference';

/**
 * O tema da aplicação, em três estados: seguir o sistema, claro, escuro.
 *
 * O provider guarda a **escolha** (`Tema | null`), não o tema em vigor. É o que
 * mantém "seguir o sistema" sendo um estado de verdade e não um valor inicial
 * que se perde: enquanto a escolha for `null`, trocar o tema do sistema
 * operacional alcança a aplicação sem recarregar.
 *
 * A primeira pintura não passa por aqui — quem aplica o tema antes dela é o
 * script síncrono do `index.html`. Este provider assume a partir daí.
 */

interface TemaContextData {
  /** O tema desenhado agora, já resolvido. */
  tema: Tema;
  /** `true` quando há escolha explícita gravada — é o que revela "Seguir o sistema". */
  temEscolhaExplicita: boolean;
  /** Alterna claro ↔ escuro, gravando a escolha. */
  alternar: () => void;
  /** Descarta a escolha e volta a acompanhar o sistema operacional. */
  seguirSistema: () => void;
}

const TemaContext = createContext<TemaContextData>({} as TemaContextData);

export function TemaProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [escolha, setEscolha] = useState<Tema | null>(() => lerTemaEscolhido());
  const [doSistema, setDoSistema] = useState<Tema>(() => temaDoSistema());

  // Sem escolha explícita, o sistema manda — inclusive quando ele muda com a
  // aplicação aberta (um agendamento de tema noturno, por exemplo).
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setDoSistema(e.matches ? 'escuro' : 'claro');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const tema = escolha ?? doSistema;

  useEffect(() => {
    aplicarTemaNoDocumento(escolha);
  }, [escolha]);

  const alternar = useCallback(() => {
    // Alterna a partir do que está *na tela*, não da escolha: quem está em
    // escuro por causa do sistema e aciona o controle espera ir para o claro,
    // não descobrir que o primeiro clique não fez nada visível.
    const proximo: Tema = tema === 'escuro' ? 'claro' : 'escuro';
    gravarTemaEscolhido(proximo);
    setEscolha(proximo);
  }, [tema]);

  const seguirSistema = useCallback(() => {
    limparTemaEscolhido();
    setDoSistema(temaDoSistema());
    setEscolha(null);
  }, []);

  const valor = useMemo(
    () => ({ tema, temEscolhaExplicita: escolha !== null, alternar, seguirSistema }),
    [tema, escolha, alternar, seguirSistema],
  );

  return <TemaContext.Provider value={valor}>{children}</TemaContext.Provider>;
}

export function useTema(): TemaContextData {
  return useContext(TemaContext);
}
